import { Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import React from "react";

interface PricingCardProps {
  name: string;
  price: string;
  description: string;
  features?: string[];
  cta: string;
  popular?: boolean;
  planKey: string;
  comingSoon?: boolean;
  onCtaClick?: () => void;
}

export function PricingCard({
  name,
  price,
  description,
  features = [],
  cta,
  popular,
  planKey,
  comingSoon,
  onCtaClick,
}: PricingCardProps) {
  return (
    <div
      className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl flex flex-col justify-between h-full ${
        popular ? "border-green-500 ring-4 ring-green-100" : "border-gray-200 hover:border-green-200"
      }`}
    >
      {/* Popular badge */}
      {popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-green-500 text-white px-6 py-2 rounded-full text-sm font-medium flex items-center space-x-1 whitespace-nowrap">
            {/* Inline SVG star icon */}
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.385 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.385-2.46a1 1 0 00-1.175 0l-3.385 2.46c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118l-3.385-2.46c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.967z" /></svg>
            <span>Most Popular</span>
          </div>
        </div>
      )}
      <div className="p-8 flex flex-col flex-1">
        {/* Plan header */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
            {name}
          </h3>
          <div className="mb-4">
            <span className="text-4xl font-bold text-gray-900">{price}</span>
            <span className="text-gray-600">/month</span>
          </div>
          {/* Adjusted description for consistent height */}
          <p className="text-gray-600 min-h-[56px] flex items-center justify-center text-center leading-snug">
            {description}
          </p>
        </div>
        {/* Features and CTA aligned at the bottom */}
        <div className="flex flex-col flex-1 justify-start">
          <div className="space-y-4 mb-8 min-h-[120px] flex flex-col justify-start">
            {features.map((feature, featureIndex) => (
              <div key={featureIndex} className="flex items-center space-x-3">
                {/* Inline SVG checkmark icon */}
                <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
          <Button
            disabled={!!comingSoon}
            className={`w-full py-3 text-lg mt-auto ${
              popular
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-gray-900 hover:bg-gray-800 text-white"
            } ${comingSoon ? "bg-gray-300 text-gray-500 cursor-not-allowed" : ""}`}
            onClick={onCtaClick}
          >
            {cta}
          </Button>
        </div>
      </div>
    </div>
  );
} 